import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  collection,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
  doc,
} from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase-client';
import { startDirectMessage } from '../utils/messages';
import { getLocalPlatformFee } from '../utils/pricingCopy';
import { extractSupportAdminIds } from '../utils/supportAdmins';
import '../styles/help.css';

const CATEGORY_LABELS = {
  new_feature: 'New feature',
  improvement: 'Improvement',
  fix: 'Fix',
  announcement: 'Announcement',
};

function formatReleaseDate(value) {
  if (!value) return '';
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

export function isProductUpdateCorrected(update = {}) {
  const published = timestampMillis(update.publishedAt);
  const updated = timestampMillis(update.updatedAt);
  return Boolean(published && updated > published + 1000);
}

export function ProductUpdateArticle({ update, featured = false, selected = false }) {
  if (!update) return null;
  return (
    <article
      id={`product-update-${update.id}`}
      className={[
        'product-update',
        featured ? 'product-update--featured' : '',
        selected ? 'product-update--selected' : '',
      ].filter(Boolean).join(' ')}
    >
      <header className="product-update__header">
        <div className="product-update__meta">
          <time dateTime={update.releaseDate}>{formatReleaseDate(update.releaseDate)}</time>
          {isProductUpdateCorrected(update) && <span>Updated</span>}
        </div>
        <h2>{update.title}</h2>
        <p>{update.summary}</p>
      </header>

      <div className="product-update__sections">
        {(Array.isArray(update.sections) ? update.sections : []).map((section, sectionIndex) => {
          const sectionCategory = section.category;
          return (
            <details
              key={`${section.heading}-${sectionIndex}`}
              className={`product-update-section product-update-section--${sectionCategory}`}
              open={featured && sectionIndex === 0}
            >
              <summary>
                <span className={`product-update__category product-update__category--${sectionCategory}`}>
                  {CATEGORY_LABELS[sectionCategory] || 'Update'}
                </span>
                <strong>{section.heading}</strong>
                <span className="product-update-section__toggle" aria-hidden="true" />
              </summary>
              <div className="product-update-section__body">
                <ul>
                  {(Array.isArray(section.items) ? section.items : []).map((item, itemIndex) => (
                    <li key={`${item}-${itemIndex}`}>{item}</li>
                  ))}
                </ul>
              </div>
            </details>
          );
        })}
      </div>

      {update.action?.url && update.action?.label && (
        <Link className="button button-primary product-update__action" to={update.action.url}>
          {update.action.label}
        </Link>
      )}
    </article>
  );
}

function HelpTopic({ title, items }) {
  return (
    <section className="help-topic">
      <h2>{title}</h2>
      <div className="help-faq">
        {items.map((item) => (
          <details key={item.q} className="help-faq__item">
            <summary>{item.q}</summary>
            <div className="help-faq__answer">{item.a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}

export default function Help() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedUpdateId = searchParams.get('update') || '';
  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab === 'updates' || selectedUpdateId ? 'updates' : 'help';
  const [supportAdmin, setSupportAdmin] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(true);
  const [updatesError, setUpdatesError] = useState('');
  const [expandedYears, setExpandedYears] = useState([]);
  const { feeLabel: localFeeLabel } = useMemo(() => getLocalPlatformFee('GBP'), []);

  useEffect(() => {
    let cancelled = false;
    const loadSupportAdmin = async () => {
      try {
        const snap = await getDoc(doc(db, 'appSettings', 'support'));
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        const firstUid = extractSupportAdminIds(data)[0];
        if (firstUid) setSupportAdmin({ id: firstUid, name: 'Support' });
      } catch (err) {}
    };
    loadSupportAdmin();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadUpdates = async () => {
      setUpdatesLoading(true);
      setUpdatesError('');
      try {
        const updatesQuery = query(
          collection(db, 'productUpdates'),
          where('status', '==', 'published'),
          orderBy('releaseDate', 'desc')
        );
        const snap = await getDocs(updatesQuery);
        if (!cancelled) {
          setUpdates(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
        }
      } catch (err) {
        if (!cancelled) {
          setUpdates([]);
          setUpdatesError('We could not load product updates just now. Please try again later.');
        }
      } finally {
        if (!cancelled) setUpdatesLoading(false);
      }
    };
    loadUpdates();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTabChange = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    if (tab === 'help') next.delete('update');
    setSearchParams(next, { replace: false });
  };

  const handleContactSupport = async () => {
    if (!user) {
      window.location.href = 'mailto:support@member-nest.com';
      return;
    }
    if (!supportAdmin) {
      window.alert('Support is currently unavailable.');
      return;
    }
    try {
      const thread = await startDirectMessage(user, supportAdmin);
      navigate(`/messages/${thread.id}`);
    } catch (err) {
      window.alert('Could not start chat with support.');
    }
  };

  const helpTopics = [
    {
      title: 'Getting started',
      items: [
        {
          q: 'How do I find and join a club?',
          a: user
            ? <>Open <Link to="/teams">Clubs</Link>, search for your club, and follow its joining instructions.</>
            : <>Create an account or sign in, then search for your club from the Clubs page.</>,
        },
        {
          q: 'How do I pay for my membership?',
          a: <>Clubs can take membership payment through Member Nest and Stripe, hold access until they confirm an external payment, or let members in and resolve payment later. Paid events, courses, bolt-ons and similar features still require the club to finish Stripe setup.</>,
        },
        {
          q: 'Where can I see my clubs and upcoming activity?',
          a: user
            ? <>Your <Link to="/dashboard">Home</Link> page brings together your clubs, upcoming activity, and recent notifications.</>
            : <>Once signed in, your Home page brings together your clubs, upcoming activity, and recent notifications.</>,
        },
      ],
    },
    {
      title: 'Account and payments',
      items: [
        {
          q: 'How do I reset my password?',
          a: <>Use <Link to="/reset-password">Forgot password</Link> and enter the email address attached to your account.</>,
        },
        {
          q: 'How do I update my details or notification preferences?',
          a: user
            ? <>Open your <Link to="/profile">Profile</Link> to manage your details and communication preferences.</>
            : <>Sign in, then open Profile to manage your details and communication preferences.</>,
        },
        {
          q: 'What is the platform fee?',
          a: <>This is Member Nest’s service charge per member, currently {localFeeLabel} per year.</>,
        },
      ],
    },
  ];

  const { latestUpdate, olderUpdates, olderByYear, archiveYears } = useMemo(() => {
    const latest = updates[0] || null;
    const older = updates.slice(1);
    const byYear = older.reduce((groups, update) => {
      const year = String(update.releaseDate || '').slice(0, 4) || 'Earlier';
      groups[year] = [...(groups[year] || []), update];
      return groups;
    }, {});
    const years = Object.keys(byYear).sort((first, second) => {
      if (first === 'Earlier') return 1;
      if (second === 'Earlier') return -1;
      return second.localeCompare(first);
    });
    return {
      latestUpdate: latest,
      olderUpdates: older,
      olderByYear: byYear,
      archiveYears: years,
    };
  }, [updates]);

  useEffect(() => {
    if (updatesLoading || archiveYears.length === 0) return;
    const selectedUpdate = olderUpdates.find((update) => update.id === selectedUpdateId);
    const selectedYear = selectedUpdate
      ? String(selectedUpdate.releaseDate || '').slice(0, 4) || 'Earlier'
      : '';
    const yearToOpen = selectedYear || archiveYears[0];
    setExpandedYears((current) => (
      current.includes(yearToOpen) ? current : [...current, yearToOpen]
    ));
  }, [archiveYears, olderUpdates, selectedUpdateId, updatesLoading]);

  useEffect(() => {
    if (!selectedUpdateId || updatesLoading) return;
    const target = document.getElementById(`product-update-${selectedUpdateId}`);
    target?.scrollIntoView?.({ block: 'start' });
  }, [selectedUpdateId, updatesLoading, expandedYears]);

  const handleYearToggle = (year, isOpen) => {
    setExpandedYears((current) => {
      if (isOpen) return current.includes(year) ? current : [...current, year];
      return current.filter((item) => item !== year);
    });
  };

  return (
    <main className="marketing-root help-root">
      <div className="container help-shell">
        <header className="help-hero">
          <p className="marketing-kicker">Member Nest</p>
          <h1>How can we help?</h1>
          <p>Find practical answers, contact our team, or catch up on the latest improvements to Member Nest.</p>
        </header>

        <div className="help-tabs" role="tablist" aria-label="Help sections">
          <button
            id="help-tab"
            type="button"
            role="tab"
            aria-selected={activeTab === 'help'}
            aria-controls="help-panel"
            className={activeTab === 'help' ? 'active' : ''}
            onClick={() => handleTabChange('help')}
          >
            Help
          </button>
          <button
            id="updates-tab"
            type="button"
            role="tab"
            aria-selected={activeTab === 'updates'}
            aria-controls="updates-panel"
            className={activeTab === 'updates' ? 'active' : ''}
            onClick={() => handleTabChange('updates')}
          >
            What’s New
          </button>
        </div>

        {activeTab === 'help' ? (
          <div id="help-panel" role="tabpanel" aria-labelledby="help-tab" className="help-panel">
            <section className="help-actions" aria-label="Support options">
              <article className="help-action-card help-action-card--primary">
                <span className="help-action-card__eyebrow">{user ? 'Chat with us' : 'Email us'}</span>
                <h2>Need a hand?</h2>
                <p>
                  {user
                    ? 'Start a private conversation with Member Nest support and we’ll take it from there.'
                    : 'Send our support team an email and we’ll get back to you as soon as we can.'}
                </p>
                <button type="button" className="button button-primary" onClick={handleContactSupport}>
                  {user ? 'Message support' : 'Email support'}
                </button>
              </article>

              <article className="help-action-card">
                <span className="help-action-card__eyebrow">{user ? 'Your account' : 'Already a member?'}</span>
                <h2>{user ? 'Quick links' : 'Sign in for personal help'}</h2>
                <p>
                  {user
                    ? 'Jump back to the places you use most.'
                    : 'Sign in to message support and access your clubs, payments, and profile.'}
                </p>
                <div className="help-quick-links">
                  {user ? (
                    <>
                      <Link to="/teams">My clubs</Link>
                      <Link to="/profile">My profile</Link>
                      <Link to="/messages">Messages</Link>
                    </>
                  ) : (
                    <>
                      <Link to="/login">Sign in</Link>
                      <Link to="/register">Create account</Link>
                    </>
                  )}
                </div>
              </article>
            </section>

            <div className="help-topics">
              {helpTopics.map((topic) => (
                <HelpTopic key={topic.title} {...topic} />
              ))}
            </div>

            <footer className="help-policies">
              <h2>Policies and legal information</h2>
              <p>
                Read our <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
                <Link to="/terms-of-service">Terms of Service</Link>.
              </p>
            </footer>
          </div>
        ) : (
          <div id="updates-panel" role="tabpanel" aria-labelledby="updates-tab" className="help-panel updates-panel">
            <header className="updates-heading">
              <div>
                <p className="marketing-kicker">Product updates</p>
                <h2>What’s new at Member Nest</h2>
              </div>
              <p>New tools, thoughtful improvements, and fixes that make running your club simpler.</p>
            </header>

            {updatesLoading ? (
              <div className="updates-state" role="status">Loading product updates…</div>
            ) : updatesError ? (
              <div className="updates-state updates-state--error" role="alert">{updatesError}</div>
            ) : !latestUpdate ? (
              <div className="updates-state">
                <h2>Updates are on their way</h2>
                <p>We’ll share new Member Nest releases here.</p>
              </div>
            ) : (
              <>
                <ProductUpdateArticle
                  update={latestUpdate}
                  featured
                  selected={selectedUpdateId === latestUpdate.id}
                />
                {archiveYears.length > 0 && (
                  <section className="updates-archive" aria-labelledby="earlier-updates-heading">
                    <h2 id="earlier-updates-heading" className="updates-archive-heading">Earlier updates</h2>
                    {archiveYears.map((year) => {
                      const yearUpdates = olderByYear[year];
                      return (
                        <details
                          key={year}
                          className="updates-year-group"
                          open={expandedYears.includes(year)}
                          onToggle={(event) => handleYearToggle(year, event.currentTarget.open)}
                        >
                          <summary>
                            <strong>{year}</strong>
                            <span>
                              {yearUpdates.length} {yearUpdates.length === 1 ? 'update' : 'updates'}
                            </span>
                            <span className="updates-year-group__toggle" aria-hidden="true" />
                          </summary>
                          <div className="updates-year-group__list">
                            {yearUpdates.map((update) => (
                              <ProductUpdateArticle
                                key={update.id}
                                update={update}
                                selected={selectedUpdateId === update.id}
                              />
                            ))}
                          </div>
                        </details>
                      );
                    })}
                  </section>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
